import React, { useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import AuthRightSection from '../components/AuthRightSection';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../services/api';
import { getApiErrorMessage } from '../utils/errors';

type Status = 'ready' | 'verifying' | 'verified' | 'invalid';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user, refreshUser } = useAuth();

  // Mail scanners open links automatically, so the link only takes effect once the
  // reader confirms here; otherwise a scanner could verify an account someone else set up
  const [status, setStatus] = useState<Status>(token ? 'ready' : 'invalid');
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleVerify = async () => {
    if (!token) return;
    setError('');
    setStatus('verifying');

    try {
      await authApi.confirmEmailVerification(token);
      setStatus('verified');
      if (user) {
        // Hides the "verify your email" banner; nothing else depends on it
        refreshUser().catch(() => {});
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        setStatus('invalid');
      } else {
        setStatus('ready');
        setError(getApiErrorMessage(err, 'Unable to verify your email. Please try again.'));
      }
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMessage('');

    try {
      const response = await authApi.requestEmailVerification();
      setResendMessage(response.message);
    } catch (err) {
      setResendMessage(getApiErrorMessage(err, 'Failed to send a new link. Please try again.'));
    } finally {
      setResending(false);
    }
  };

  const continueLink = user ? (
    <Link
      to="/dashboard"
      className="border border-[#1f1f1f] border-solid px-6 py-3 font-['Ovo',_serif] text-[#000000] text-[18px] uppercase bg-transparent hover:bg-[#1f1f1f] hover:text-white transition-colors text-center"
    >
      GO TO DASHBOARD
    </Link>
  ) : (
    <Link
      to="/signin"
      className="border border-[#1f1f1f] border-solid px-6 py-3 font-['Ovo',_serif] text-[#000000] text-[18px] uppercase bg-transparent hover:bg-[#1f1f1f] hover:text-white transition-colors text-center"
    >
      SIGN IN
    </Link>
  );

  return (
    <div
      className="bg-[#ffffff] relative w-full min-h-screen flex flex-col lg:flex-row"
      data-name="Verify Email"
    >
      {/* Left side - Content */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-8 sm:gap-12 lg:gap-20 items-center justify-center max-w-[673px] w-full">
          <div className="flex flex-col gap-8 items-start justify-start w-full">
            {status === 'invalid' ? (
              <div className="flex flex-col gap-6 items-center justify-center w-full text-center">
                {/* Error icon */}
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>

                <div className="font-['Ovo',_serif] text-[#000000] text-[24px] sm:text-[28px] lg:text-[32px] uppercase">
                  INVALID VERIFICATION LINK
                </div>

                <div className="font-['Ovo',_serif] font-normal text-[#767472] text-[16px] max-w-md">
                  This verification link has expired, has already been used, or was replaced by a
                  newer one. Please use the latest link we sent you.
                </div>

                {resendMessage && (
                  <div
                    className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px] max-w-md"
                    role="status"
                  >
                    {resendMessage}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mt-4">
                  {user && user.email_verified === false && (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resending}
                      className="border border-[#c6a664] border-solid px-6 py-3 font-['Ovo',_serif] text-[#c6a664] text-[18px] uppercase bg-transparent hover:bg-[#c6a664] hover:text-white transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resending ? 'SENDING...' : 'SEND A NEW LINK'}
                    </button>
                  )}
                  {continueLink}
                </div>
              </div>
            ) : status === 'verified' ? (
              <div className="flex flex-col gap-6 items-center justify-center w-full text-center">
                {/* Success icon */}
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                <div className="font-['Ovo',_serif] text-[#000000] text-[24px] sm:text-[28px] lg:text-[32px] uppercase">
                  EMAIL VERIFIED
                </div>

                <div className="font-['Ovo',_serif] font-normal text-[#767472] text-[16px] max-w-md">
                  Your email address has been verified. Thank you!
                </div>

                {continueLink}
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex flex-col gap-4 items-start justify-center w-full">
                  <div className="font-['Ovo',_serif] text-[#000000] text-[24px] sm:text-[28px] lg:text-[32px] text-center uppercase w-full">
                    VERIFY YOUR EMAIL
                  </div>
                  <div className="font-['Ovo',_serif] font-normal text-[#767472] text-[16px] text-center w-full">
                    Confirm that this email address belongs to your CLOUT account. Only continue if
                    you created the account or changed its email to this address.
                  </div>
                </div>

                {/* Error message */}
                {error && (
                  <div
                    className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded w-full"
                    role="alert"
                  >
                    {error}
                  </div>
                )}

                <div className="flex flex-col items-center justify-center w-full">
                  <button
                    type="button"
                    onClick={handleVerify}
                    className="border border-[#1f1f1f] border-solid px-6 py-3 font-['Ovo',_serif] text-[#000000] text-[20px] uppercase bg-transparent hover:bg-[#1f1f1f] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                    disabled={status === 'verifying'}
                  >
                    {status === 'verifying' ? 'VERIFYING...' : 'VERIFY EMAIL'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right side - Illustration */}
      <div className="hidden md:block flex-1 min-h-[400px] lg:min-h-screen">
        <AuthRightSection />
      </div>
    </div>
  );
};

export default VerifyEmail;
